import {
  Box,
  Button,
  Divider,
  Skeleton,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import { useCreateInterest } from "../../hooks/tenants/useCreateInterest";
import theme from "../../styles/styleTheme";
import BookViewing from "../Events/BookViewing";
import LoginOrCreateAccount from "../elements/LoginOrCreateAccount";
import { useEvents } from "../../hooks/events/useEvents";
import { BoxPaper } from "../../pages/PropertyDetails";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import NAV from "../../utils/nav.const";

type PropertyDetailsViewingProps = {
  property: any;
};

const PropertyDetailsViewing = ({ property }: PropertyDetailsViewingProps) => {
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isInterested, setIsInterested] = useState(true);
  const [subscribeText, setSubscribeText] = useState(
    t("viewing.subscribeText_default"), //! Not working, not switching to english
  );
  const [hasViewing, setHasViewing] = useState(false);

  const { user } = useUserAuth();
  const { createInterest } = useCreateInterest();
  const {
    getDataById: getEventsByPropertyId,
    data: events,
    loading: loadingEvents,
  } = useEvents();

  const location = useLocation();
  const redirectTo = location.pathname + location.search;

  useEffect(() => {
    setIsLoggedIn(user != null);

    if (user != null && property != null) {
      setIsInterested(
        user.tenant.candidatures.some((x: any) => x.propertyId === property.id),
      );
    }

    if (property != null) {
      getEventsByPropertyId(property.id);
    }
  }, [user, property]);

  useEffect(() => {
    if (events.length > 0) {
      setHasViewing(true);
    }
  }, [events]);

  const handleShowInterest = () => {
    const sendEmail = true;
    createInterest(user.tenant.id, property.id, sendEmail);
    setSubscribeText(t("viewing.subscribeText_interested"));
    setIsInterested(true);
  };

  return (
    <BoxPaper
      style={{
        marginTop: "24px",
        background: property?.isRented ? theme.palette.primary.light : "#fff",
      }}
    >
      {!property?.isRented && (
        <Typography variant="h3" marginBottom={1}>
          {t("viewing.plan_viewing")}
        </Typography>
      )}
      {loadingEvents ? (
        <>
          <Skeleton variant="text" width={"80%"} height={30} />
          <Skeleton variant="text" width={"90%"} height={30} />
          <Skeleton variant="text" width={"60%"} height={30} />
          <Skeleton variant="text" width={"85%"} height={30} />
        </>
      ) : (
        <>
          <>
            {property?.isRented ? (
              <Box
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                color={"#fff"}
              >
                <Box>
                  <ErrorOutlineIcon color="error" sx={{ marginRight: 1 }} />
                </Box>
                <Typography color={"#000"} variant="h3">
                  {t("viewing.recently_rented")}
                </Typography>
              </Box>
            ) : (
              <>
                {isLoggedIn ? (
                  <>
                    {!property?.isRented && (
                      <>
                        {hasViewing ? (
                          <BookViewing property={property} />
                        ) : (
                          <>
                            <Typography variant="subtitle2" marginBottom={3}>
                              {subscribeText}
                            </Typography>
                            <Button
                              onClick={handleShowInterest}
                              disabled={isInterested}
                              variant="contained"
                              color="secondary"
                              sx={{
                                width: "100%",
                                bgcolor: theme.palette.secondary.main,
                                color: "#fff",
                              }}
                            >
                              {t(
                                !isInterested
                                  ? "viewing.show_interest"
                                  : "viewing.subscribed",
                              )}
                            </Button>
                          </>
                        )}
                      </>
                    )}
                    
                    {(user && !user?.isPremium) && (
                      <>
                        <Divider
                          sx={{
                            margin: "23px 0",
                            borderBottomWidth: 2.5,
                            borderColor: "#eee",
                          }}
                        />
                        <Box>
                          <>
                            <Typography variant="h3" marginBottom={1}>
                              {t("viewing.upgradeAccount-title")}
                            </Typography>
                            <Typography variant="subtitle2" marginBottom={3}>
                              {t("viewing.upgradeAccount-text")}
                            </Typography>
                            <Button
                              variant="outlined"
                              color="secondary"
                              sx={{ width: "100%" }}
                              component={Link}
                              to={`/${NAV.PREMIUM_UPGRADE}`}
                            >
                              {t("viewing.upgradeAccount-button")}
                            </Button>
                          </>
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <LoginOrCreateAccount
                    title={t("viewing.login_or_create_account")}
                    redirectTo={redirectTo}
                  />
                )}
              </>
            )}
          </>
        </>
      )}
    </BoxPaper>
  );
};

export const StyledLink = styled(Link)({
  textDecoration: "none",
  color: "#000",
  fontFamily: "Quicksand, sans-serif",
  fontSize: 14,

  ":hover": {
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
});

export default PropertyDetailsViewing;
